/**
 * Shopify Blog Article Auto H2 Table of Contents
 * Fixes sticky/fixed header offset issues for TOC anchor jumps.
 */
(function () {
  'use strict';

  const CONFIG = {
    contentSelector: '#article-content',
    tocContainerId: 'article-toc-container',
    tocTitle: 'Table of Contents',

    headerSelectors: [
      'sticky-header',
      '.shopify-section-header-sticky',
      '#shopify-section-header.shopify-section-header-sticky',
      '#shopify-section-header',
      '.header-wrapper.header-wrapper--sticky',
      '.header-wrapper',
      'header.header',
      '.announcement-bar',
      '.utility-bar',
      '[data-header-sticky]'
    ],

    extraOffset: 24,
    minOffset: 120,
    idPrefix: 'h2-'
  };

  function slugify(text) {
    return text
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[\s\u3000]+/g, '-')
      .replace(/[^\w\u4e00-\u9fa5\-]/g, '')
      .replace(/\-+/g, '-')
      .replace(/^\-+|\-+$/g, '');
  }

  function generateUniqueId(baseId, usedIds) {
    let id = baseId || CONFIG.idPrefix + 'section';
    let counter = 1;
    const original = id;

    while (usedIds.has(id) || document.getElementById(id)) {
      id = original + '-' + counter;
      counter += 1;
    }

    usedIds.add(id);
    return id;
  }

  function parsePx(value) {
    const number = parseFloat(value);
    return Number.isFinite(number) ? number : 0;
  }

  function getCssOffset() {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue('--toc-scroll-offset');
    return parsePx(value);
  }

  function getHeaderOffset() {
    let maxBottom = 0;

    CONFIG.headerSelectors.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (el) {
        const rect = el.getBoundingClientRect();
        const style = window.getComputedStyle(el);
        const isVisible = rect.height > 0 && rect.width > 0 && style.display !== 'none' && style.visibility !== 'hidden';
        const isStickyOrFixed = style.position === 'fixed' || style.position === 'sticky';
        const isNearTop = rect.top <= 5 && rect.bottom > 0;

        if (isVisible && isStickyOrFixed && isNearTop) {
          maxBottom = Math.max(maxBottom, rect.bottom);
        }
      });
    });

    const cssOffset = getCssOffset();
    const detectedOffset = maxBottom + CONFIG.extraOffset;

    return Math.max(detectedOffset, cssOffset, CONFIG.minOffset);
  }

  function updateScrollMargin() {
    const offset = getHeaderOffset();
    document.documentElement.style.setProperty('--toc-scroll-offset', offset + 'px');
  }

  function scrollToTarget(target, behavior) {
    if (!target) return;

    updateScrollMargin();

    const offset = getHeaderOffset();
    const targetTop = target.getBoundingClientRect().top + window.pageYOffset - offset;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: behavior || 'smooth'
    });
  }

  function smoothScrollTo(targetId, event) {
    if (event) event.preventDefault();

    const target = document.getElementById(targetId);
    if (!target) return;

    scrollToTarget(target, 'smooth');

    if (history.pushState) {
      history.pushState(null, '', '#' + targetId);
    }
  }

  function buildTOC() {
    const contentBlock = document.querySelector(CONFIG.contentSelector);
    if (!contentBlock) {
      console.warn('[TOC] Content block not found: ' + CONFIG.contentSelector);
      return;
    }

    const existingToc = document.getElementById(CONFIG.tocContainerId);
    if (existingToc) {
      existingToc.remove();
    }

    const h2Elements = Array.from(contentBlock.querySelectorAll('h2'))
      .filter(function (h2) {
        return !h2.closest('.article-toc');
      });

    if (h2Elements.length === 0) return;

    const usedIds = new Set();
    const tocItems = [];

    h2Elements.forEach(function (h2, index) {
      let id = h2.id;

      if (!id) {
        const baseId = slugify(h2.textContent) || CONFIG.idPrefix + (index + 1);
        id = generateUniqueId(baseId, usedIds);
        h2.id = id;
      } else {
        usedIds.add(id);
      }

      tocItems.push({
        id: id,
        text: h2.textContent.trim()
      });
    });

    const tocContainer = document.createElement('nav');
    tocContainer.id = CONFIG.tocContainerId;
    tocContainer.className = 'article-toc';
    tocContainer.setAttribute('aria-label', 'Table of contents');

    const tocTitle = document.createElement('div');
    tocTitle.className = 'article-toc__title';
    tocTitle.textContent = CONFIG.tocTitle;
    tocContainer.appendChild(tocTitle);

    const tocList = document.createElement('ul');
    tocList.className = 'article-toc__list';

    tocItems.forEach(function (item) {
      const li = document.createElement('li');
      li.className = 'article-toc__item';

      const a = document.createElement('a');
      a.className = 'article-toc__link';
      a.href = '#' + item.id;
      a.textContent = item.text;
      a.addEventListener('click', function (event) {
        smoothScrollTo(item.id, event);
      });

      li.appendChild(a);
      tocList.appendChild(li);
    });

    tocContainer.appendChild(tocList);

    h2Elements[0].parentNode.insertBefore(tocContainer, h2Elements[0]);

    updateScrollMargin();
  }

  function handleInitialHash() {
    if (!window.location.hash) return;

    setTimeout(function () {
      const target = document.getElementById(window.location.hash.slice(1));
      scrollToTarget(target, 'auto');
    }, 250);
  }

  function init() {
    buildTOC();
    handleInitialHash();

    let resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateScrollMargin, 150);
    });

    window.addEventListener('load', function () {
      updateScrollMargin();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
